import { createFileRoute } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import type { BlogPostSummary } from "~/lib/blog-queries";
import { getBlogPostsByTag, type BlogPost as StaticBlogPost } from "~/content/blog";
import { breadcrumbSchema, schemaGraph, blogSchema } from "~/lib/seo/schema";
import { buildMeta } from "~/lib/seo/meta";
import { JsonLd } from "~/components/seo/JsonLd";
import { serializeForServer } from "~/lib/server-serialization";
import { getLocale } from "~/paraglide/runtime";
import { BlogPostLink, LocalizedBlogLink } from "~/components/blog/BlogLink";
import { getBlogCanonical, getBlogHeadLinks } from "~/lib/blog-seo";

// ── Server loader ───────────────────────────────────────────────────────────

const fetchTagPosts = createServerFn({ method: "GET" })
  .validator(z.string())
  .handler(async ({ data: tag }) => {
    // Decode URL-encoded tag
    const decoded = decodeURIComponent(tag);

    // Try DB first
    const { getPostsByTag } = await import("~/lib/blog-queries");
    const locale = getLocale();
    const dbPosts = await getPostsByTag(decoded, locale);
    if (dbPosts.length > 0) {
      return serializeForServer({ tag: decoded, posts: dbPosts, locale, source: "db" as const });
    }

    // Fallback: static
    const staticPosts = getBlogPostsByTag(decoded);
    const mapped: BlogPostSummary[] = staticPosts
      .filter((p) => p.locale === locale)
      .map((p) => ({
        id: p.slug,
        locale: p.locale,
        slug: p.slug,
        title: p.title,
        excerpt: p.description,
        contentType: "editorial" as const,
        pageType: "editorial" as const,
        tags: p.tags,
        author: p.author,
        readingTimeMin: p.readingTimeMin,
        countryCode: null,
        city: null,
        roleCategory: null,
        publishedAt: p.publishedAt as unknown as Date,
        updatedAt: p.publishedAt as unknown as Date,
      }));

    return serializeForServer({ tag: decoded, posts: mapped, locale, source: "static" as const });
  });

// ── Route ───────────────────────────────────────────────────────────────────

export const Route = createFileRoute("/blog/tags/$tag")({
  loader: ({ params }) => fetchTagPosts({ data: params.tag }),
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const { tag, locale } = loaderData;
    const seoPage = { locale, pageType: "tag", slug: encodeURIComponent(tag) };
    return {
      meta: buildMeta({
        title: `${tag} — Blog | Alchema`,
        description: `Articles tagged "${tag}" for EU job seekers and career professionals.`,
        url: getBlogCanonical(seoPage).path,
      }),
      links: getBlogHeadLinks(seoPage),
    };
  },
  component: TagPage,
});

// ── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(date: Date | string | null): string {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

// ── Page ────────────────────────────────────────────────────────────────────

function TagPage() {
  const { tag, posts, locale } = Route.useLoaderData();

  const jsonLd = schemaGraph(
    blogSchema(locale),
    breadcrumbSchema([
      { name: "Home", url: locale === "en" ? "/" : `/${locale}` },
      { name: "Blog", url: getBlogCanonical({ locale, pageType: "index" }).path },
      { name: tag, url: getBlogCanonical({ locale, pageType: "tag", slug: encodeURIComponent(tag) }).path },
    ]),
  );

  return (
    <div style={{ backgroundColor: "var(--bg)" }}>
      <JsonLd json={jsonLd} />

      {/* Header */}
      <section className="mx-auto max-w-3xl px-6 pt-24 pb-8">
        <nav
          aria-label="Breadcrumb"
          className="mb-8 text-[0.8125rem]"
          style={{ color: "var(--text-muted)" }}
        >
          <ol className="flex items-center gap-1.5">
            <li>
              <LocalizedBlogLink href="/blog" className="transition-colors duration-150 hover:text-[var(--text)]">
                Blog
              </LocalizedBlogLink>
            </li>
            <li aria-hidden="true" className="opacity-40">/</li>
            <li style={{ color: "var(--text-secondary)" }}>{tag}</li>
          </ol>
        </nav>

        <h1
          className="text-[clamp(2rem,5vw,3rem)] font-bold leading-[1.1] tracking-[-0.025em]"
          style={{ color: "var(--text)", fontFamily: "var(--font-display)" }}
        >
          {tag}
        </h1>
        <p
          className="mt-3 text-[0.8125rem]"
          style={{ color: "var(--text-muted)" }}
        >
          {posts.length} article{posts.length !== 1 ? "s" : ""}
        </p>
      </section>

      {/* Post list */}
      <section className="mx-auto max-w-3xl px-6 pb-32">
        {posts.length === 0 ? (
          <div className="py-16 text-center">
            <p className="text-sm" style={{ color: "var(--text-muted)" }}>
              No articles with this tag yet.
            </p>
            <LocalizedBlogLink
              href="/blog"
              className="mt-4 inline-flex text-sm font-medium transition-colors duration-150 hover:text-[var(--accent)]"
              style={{ color: "var(--text-secondary)" }}
            >
              &larr; View all articles
            </LocalizedBlogLink>
          </div>
        ) : (
          posts.map((post: BlogPostSummary) => (
            <BlogPostLink
              key={`${post.locale}:${post.slug}`}
              post={post}
              className="group block"
            >
              <article
                className="py-7"
                style={{ borderBottom: "1px solid var(--border-subtle)" }}
              >
                <div
                  className="flex items-center gap-2 text-[0.75rem] tracking-wide uppercase"
                  style={{ color: "var(--text-muted)" }}
                >
                  <time dateTime={post.publishedAt?.toString()}>
                    {formatDate(post.publishedAt)}
                  </time>
                  {post.readingTimeMin && (
                    <>
                      <span aria-hidden="true" className="opacity-40">/</span>
                      <span>{post.readingTimeMin} min</span>
                    </>
                  )}
                </div>

                <h2
                  className="mt-3 text-[clamp(1.125rem,2vw,1.375rem)] font-semibold leading-tight tracking-[-0.01em] transition-colors duration-150 group-hover:text-[var(--accent)]"
                  style={{ color: "var(--text)", fontFamily: "var(--font-display)" }}
                >
                  {post.title}
                </h2>

                {post.excerpt && (
                  <p
                    className="mt-2 text-[0.9375rem] leading-relaxed"
                    style={{ color: "var(--text-secondary)", maxWidth: "60ch" }}
                  >
                    {post.excerpt}
                  </p>
                )}
              </article>
            </BlogPostLink>
          ))
        )}
      </section>
    </div>
  );
}
